import { ArrowRightIcon } from '@heroicons/react/20/solid';
import {
  ClipboardDocumentIcon,
  DocumentCheckIcon
} from '@heroicons/react/24/outline';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { motion } from 'framer-motion';
import { analyticsService } from 'src/services/analytics/analyticsService';
import { RFQ_BUILDER_PATH_SELECTED } from 'src/services/analytics/rfqBuilderEvents';
import { Rfq } from 'src/shared/types/rfq';

import { cn } from 'src/shared/components/ui';
import { Typography } from 'src/shared/components/ui';

import RfqFilesUpload from './rfq-files/RfqFilesUpload';
import RfqImageSelection from './rfq-files/RfqImageSelection';

// icon only used in this component
const DottedCircleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeDasharray="2 2"
      fill="none"
    />
  </svg>
);

interface RfqPathSelectionProps {
  rfq?: Rfq;
  handleNext?: () => void;
}

interface PathOption {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  onClick: () => void;
}

const RfqPathSelection: React.FC<RfqPathSelectionProps> = ({ rfq }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  // When the step changes away from 'path' (via onSuccess or browser navigation),
  // return null to unmount the Drawers before AnimatePresence exit animation
  // runs — prevents framer-motion positional value conflicts with Drawer motions
  const currentStep = searchParams.get('step');
  const isExiting = currentStep !== null && currentStep !== 'path';

  const isUploadOpen =
    !isExiting &&
    (searchParams.get('files') === 'prepared-rfq' ||
      searchParams.get('files') === 'references-pegs');
  const isImageSelectionOpen =
    !isExiting && searchParams.get('files') === 'assist-me';

  const onSuccess = useCallback(() => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);

      newParams.delete('files');
      newParams.set('step', 'playground');

      return newParams;
    });
  }, [setSearchParams]);

  const trackPathSelected = (pathId: string) => {
    analyticsService.trackEvent(RFQ_BUILDER_PATH_SELECTED, {
      rfq_id: rfq?.rfq?.id,
      path: pathId
    });
  };

  const pathOptions: PathOption[] = [
    {
      id: 'prepared-rfq',
      title: 'I have a complete RFQ',
      description: 'Upload your RFQ and supporting documents',
      icon: <DocumentCheckIcon className="size-7" />,
      onClick: () => {
        trackPathSelected('prepared-rfq');
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);

          newParams.set('files', 'prepared-rfq');

          return newParams;
        });
      }
    },
    {
      id: 'references-pegs',
      title: 'I have partial details',
      description: 'Upload what you have, and we’ll help fill in the gaps',
      icon: <ClipboardDocumentIcon className="size-7" />,
      onClick: () => {
        trackPathSelected('references-pegs');
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);

          newParams.set('files', 'references-pegs');

          return newParams;
        });
      }
    },
    {
      id: 'assist-me',
      title: 'I’m starting from scratch',
      description: 'Build your RFQ with guided questions',
      icon: <DottedCircleIcon className="size-7" />,
      onClick: () => {
        trackPathSelected('assist-me');
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);

          newParams.set('files', 'assist-me');

          return newParams;
        });
      }
    }
  ];

  return (
    <motion.div
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="relative max-h-dvh w-full overflow-auto"
    >
      <div className="h-full flex flex-col mobile:px-6 px-[10%] mt-[12%] mobile:mt-[18%] items-start gap-6 relative z-30">
        <Typography
          as="h1"
          variant="h1"
          weight="bold"
          className="mobile:text-[32px] text-[40px]"
        >
          Choose how you want to build your RFQ
        </Typography>

        <div className="flex flex-col gap-4 mobile:w-full w-[70%] mt-4 relative">
          {pathOptions.map((option, index) => (
            <motion.div
              key={option.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{
                duration: 1.2,
                delay: 1.0 + index * 0.15,
                ease: 'easeInOut'
              }}
              onMouseEnter={() => setHoveredCard(option.id)}
              onMouseLeave={() => setHoveredCard(null)}
              onClick={option.onClick}
              className="flex items-center gap-6 pl-[28px] pr-[24px] py-6 rounded-xl cursor-pointer border bg-gray-1 border-gray-1 hover:shadow-sm transition-all duration-300 h-24 max-w-[843px] mobile:h-auto"
              style={
                hoveredCard === option.id
                  ? {
                      backgroundImage: `
          linear-gradient(to right, rgba(59, 130, 246, 0.1) 1px, transparent 1px),
          linear-gradient(to bottom, rgba(59, 130, 246, 0.1) 1px, transparent 1px)
        `,
                      backgroundSize: '40px 40px',
                      backgroundColor: '#FFF',
                      backgroundPositionY: '6px'
                    }
                  : undefined
              }
            >
              <div
                className={cn(
                  'flex-shrink-0 transition-colors duration-300',
                  hoveredCard === option.id ? 'text-dark' : 'text-primary'
                )}
              >
                {option.icon}
              </div>
              <div className="flex-1 flex flex-col gap-[4px]">
                <Typography
                  variant="h6"
                  weight="bold"
                  className="text-dark leading-none"
                >
                  {option.title}
                </Typography>
                <Typography variant="body-2" className="text-secondary-text">
                  {option.description}
                </Typography>
              </div>
              <div className="flex-shrink-0">
                <div
                  className={cn(
                    'size-9 rounded-full flex items-center justify-center transition-colors duration-300',
                    hoveredCard === option.id ? 'bg-primary' : 'bg-white'
                  )}
                >
                  <ArrowRightIcon
                    className={cn(
                      'size-5 transition-colors duration-300',
                      hoveredCard === option.id ? 'text-white' : 'text-primary'
                    )}
                  />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {!isExiting && (
        <>
          <RfqFilesUpload
            isOpen={isUploadOpen}
            onClose={() => {
              setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev);

                newParams.delete('files');

                return newParams;
              });
            }}
            rfq={rfq}
            onSuccess={onSuccess}
          />
          <RfqImageSelection
            isOpen={isImageSelectionOpen}
            onClose={() => {
              setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev);

                newParams.delete('files');

                return newParams;
              });
            }}
            rfq={rfq}
            onSuccess={onSuccess}
          />
        </>
      )}
    </motion.div>
  );
};

export default RfqPathSelection;
